import { Circle } from '../../CanvasGui/Circle';
import { Line } from '../../CanvasGui/Line';
import { VectorSelectionBox } from '../../CanvasGui/VectorSelectionBox';
import { Colour } from '../../Colour/Colour';
import { ColourIndex, ColouringMode } from '../../Colour/Colourable';
import { PointOps } from '../../Graphics/PointOps';
import { VectorOps } from '../../Graphics/VectorOps';
import { InputListener } from '../../Input/InputHandler';
import { Point, Vector2 } from '../../Misc/Utils';
import { VectorStruct } from '../Shapes/Structure/VectorStruct';

enum EditorMode{
    EditVertices,
    EditBezierControlPoints
}

enum VectorPointType{
    Vertex,
    ControlPoint
}

export class BVectorEditor{

    private static SMOOTHEN_MAGNITUTE = 30;

    private editorMode = EditorMode.EditVertices;

    private struct: VectorStruct
    private vertices: Point[] = []
    protected controlPoints: [Point[]] = [[]];
    
    private vertexDrawer: Circle = new Circle();
    private selectedVertexDrawer: Circle = new Circle();
    private edgeDrawer: Line = new Line();
    private controlPointDrawer: Circle = new Circle();
    private controlPointArmDrawer: Line = new Line();

    private prevSelectedPoint: number[] = [0,0,0] //first = to know if vertex/control point, second = array index, third = if control point, then first or second in the control points array
    private lastSelectedVertexIndex = 0;

    constructor(bStructure: VectorStruct, bVectorVertices: Point[], bVectorControlPoints: [Point[]]) {
        this.setupDrawers();
        this.struct = bStructure;
        this.vertices = bVectorVertices; //Copy by reference
        this.controlPoints = bVectorControlPoints; //Copy by reference
    }

    setupEditing(){
        this.lastSelectedVertexIndex = 0;
        this.editorMode = EditorMode.EditVertices;
    }

    setMode(newMode: EditorMode){
        this.editorMode = newMode;
        console.log("mode set:"+this.editorMode);
    }

    onDoubleClick(point: Point): void {
        console.log("on double click:"+point);
        if(this.isVertexClicked(point)){
            this.lastSelectedVertexIndex = this.prevSelectedPoint[1];
            this.setMode(EditorMode.EditBezierControlPoints);
            this.smoothenVertexIfNeeded(this.lastSelectedVertexIndex);
        }else{
            this.setMode(EditorMode.EditVertices);
        }
    }

    onSingleClick(point: Point): void{
        console.log("on single click:"+point);
        if(this.isVertexClicked(point)){
            this.lastSelectedVertexIndex = this.prevSelectedPoint[1];
            //this.setMode(EditorMode.EditBezierControlPoints);
        }else if(!this.isBezierControlPointClicked(point)){ //!this.isVertexClicked(point) && !this.isBezierControlPointClicked(point)
            this.setMode(EditorMode.EditVertices);
        }
    }

    isVertexClicked(localCursorPoint: Point){
        for(var i = 0; i < this.vertices.length; i++){
            if(PointOps.containedInCircle(localCursorPoint, this.vertices[i], 15)){
                this.prevSelectedPoint = [VectorPointType.Vertex, i, 0];
                return true;
            }
        }
        return false;
    }

    isBezierControlPointClicked(localCursorPoint: Point){
        //for(var i = 0; i < this.vertices.length; i++){
            var i = this.lastSelectedVertexIndex;
            if(PointOps.containedInCircle(localCursorPoint, this.controlPoints[i][0], 15)){
                this.prevSelectedPoint = [VectorPointType.ControlPoint, i, 0];
                return true;
            }

            if(PointOps.containedInCircle(localCursorPoint, this.controlPoints[i][1], 15)){
                this.prevSelectedPoint = [VectorPointType.ControlPoint, i, 1];
                return true;
            }
        //}
        return false;
    }


    /**
     * Method to know if user clicked on a vertex or control point.
     * The point details are stored in this.prevSelectedPoint
     * @param localCursorPoint 
     */
    isControlPointSelected(localCursorPoint: Point): boolean{
        var isSelected = false;

        //First Check if previously selected point is still being used to move. if so change its position
        if((this.editorMode == EditorMode.EditBezierControlPoints &&  this.getPrevSelectedPointType() == VectorPointType.ControlPoint)
        || (this.editorMode == EditorMode.EditVertices &&  this.getPrevSelectedPointType() == VectorPointType.Vertex)){

            if(PointOps.containedInCircle(localCursorPoint, this.getPrevSelectedPoint(), 15)){
                isSelected = true;
            }

        }

        if(!isSelected && this.editorMode == EditorMode.EditBezierControlPoints){
            //Secondly, check and move control points
            isSelected = this.isBezierControlPointClicked(localCursorPoint);
        }

        if(!isSelected){
            isSelected = this.isVertexClicked(localCursorPoint);
        }

        return isSelected;
    }

    editVertexAndControlPoints(localCursorPoint: Point): boolean{
        var isSelected = this.isControlPointSelected(localCursorPoint);
        
        //update the selected point with the current cursor position
        if(isSelected){
            this.setPrevSelectedPoint(localCursorPoint);
        }
        return isSelected;
    }

    private getPrevSelectedPointType(): VectorPointType{
        return this.prevSelectedPoint[0];
    }

    private getPrevSelectedPoint(): Point{
        if(this.getPrevSelectedPointType() == VectorPointType.Vertex){
            return this.vertices[this.prevSelectedPoint[1]];
        }else{
            return this.controlPoints[this.prevSelectedPoint[1]][this.prevSelectedPoint[2]];
        }
    }

    //Since we can close path using ctx.closePath, we have to make sure the first and last vertex in a polygon are same. So that bezier curves are working fine.
    private copyValuesIfNeededBasedOnIndex(vertexIndex: number){
        //Copying over values if the first or the last index is changed. To mimic full vector
        if(this.struct.isLoop()){
            var lastIndex = this.vertices.length-1
            if(vertexIndex == 0){
                this.vertices[lastIndex] = [this.vertices[vertexIndex][0], this.vertices[vertexIndex][1]];
                this.controlPoints[lastIndex][0] = [this.controlPoints[vertexIndex][0][0], this.controlPoints[vertexIndex][0][1]];
                this.controlPoints[lastIndex][1] = [this.controlPoints[vertexIndex][1][0], this.controlPoints[vertexIndex][1][1]];
            }else if(vertexIndex == lastIndex){
                this.vertices[0] = [this.vertices[vertexIndex][0], this.vertices[vertexIndex][1]];
                this.controlPoints[0][0] = [this.controlPoints[vertexIndex][0][0], this.controlPoints[vertexIndex][0][1]];
                this.controlPoints[0][1] = [this.controlPoints[vertexIndex][1][0], this.controlPoints[vertexIndex][1][1]];
            }
        }
    }

    private moveVertexAndCPByChange(vertexIndex: number, pt: Point, change: Point){
        this.vertices[vertexIndex] = [pt[0], pt[1]];
        //move control points by the change
        this.controlPoints[vertexIndex][0] = PointOps.add(this.controlPoints[vertexIndex][0], change);
        this.controlPoints[vertexIndex][1] = PointOps.add(this.controlPoints[vertexIndex][1], change);

        this.copyValuesIfNeededBasedOnIndex(vertexIndex)
    }

    private setPrevSelectedPoint(pt: Point){
        if(this.getPrevSelectedPointType() == VectorPointType.Vertex){
            var vertexIndex = this.prevSelectedPoint[1];
            var change = PointOps.subtract(pt, this.vertices[vertexIndex]);
            this.moveVertexAndCPByChange(vertexIndex, pt, change);
        }else{
            var vertexIndex = this.prevSelectedPoint[1];
            this.controlPoints[vertexIndex][this.prevSelectedPoint[2]] = [pt[0], pt[1]];
            console.log("vert:"+this.vertices[this.prevSelectedPoint[1]]);
            console.log("cp:"+this.controlPoints[this.prevSelectedPoint[1]]);
            this.copyValuesIfNeededBasedOnIndex(vertexIndex) //to make sure 
        }
    }

    smoothenVertexIfNeeded(vertexIndex: number){
        var vertex = this.vertices[vertexIndex];
        //if(PointOps.equal(vertex, this.controlPoints[vertexIndex][0]) && PointOps.equal(vertex, this.controlPoints[vertexIndex][1])){ //if the vertex was not touched by user earlier
            //vertex is relative to center.
            var unitVectorFromCenter = VectorOps.unit(PointOps.toVector2(vertex)); 
            var pcc = VectorOps.perpendicularCounterClockwise( VectorOps.multiply(BVectorEditor.SMOOTHEN_MAGNITUTE, unitVectorFromCenter) );
            var pc = VectorOps.perpendicularClockwise( VectorOps.multiply(BVectorEditor.SMOOTHEN_MAGNITUTE, unitVectorFromCenter) );
            this.controlPoints[vertexIndex][0] = PointOps.moveByVector2(vertex, pc);
            this.controlPoints[vertexIndex][1] = PointOps.moveByVector2(vertex, pcc);
            console.log("v:"+vertex+" unit:"+unitVectorFromCenter)
            this.copyValuesIfNeededBasedOnIndex(vertexIndex) //to make sure 
            //console.log("pcc:"+pcc+" pc:"+pc);
            //console.log("cp:"+this.controlPoints[vertexIndex])
        //}
    }

    draw(canvasCtx : CanvasRenderingContext2D): void{
        this.vertexDrawer.drawCircle(canvasCtx, this.vertices[0]);

        for(var i = 1; i < this.vertices.length; i++){
            //this.edgeDrawer.drawLine(canvasCtx, this.vertices[i-1], this.vertices[i]);
            if(this.editorMode == EditorMode.EditBezierControlPoints && i == this.lastSelectedVertexIndex){
                this.selectedVertexDrawer.drawCircle(canvasCtx, this.vertices[i]);
            }else{
                this.vertexDrawer.drawCircle(canvasCtx, this.vertices[i]);
            }
        }
    }

    drawControlPoints(canvasCtx : CanvasRenderingContext2D): void{
        if(this.editorMode == EditorMode.EditBezierControlPoints){
        //for(var i = 0; i < this.vertices.length; i++){
            var i = this.lastSelectedVertexIndex;
            this.controlPointDrawer.drawCircle(canvasCtx, this.controlPoints[i][0]);
            this.controlPointDrawer.drawCircle(canvasCtx, this.controlPoints[i][1]);
            
            this.controlPointArmDrawer.drawLine(canvasCtx, this.vertices[i], this.controlPoints[i][0]);
            this.controlPointArmDrawer.drawLine(canvasCtx, this.vertices[i], this.controlPoints[i][1]);
        //}
        }
    }

    private setupDrawers(){
        this.vertexDrawer.setRadius(3);
        this.vertexDrawer.setColour(ColourIndex.Fill, new Colour({h:199, s:0.3, v:0.6})); //grey
        this.vertexDrawer.setColouringMode(ColouringMode.Fill);

        this.selectedVertexDrawer.setRadius(3);
        this.selectedVertexDrawer.setColour(ColourIndex.Fill, new Colour({h:225, s:0.95, v:0.4})); //deep blue
        this.selectedVertexDrawer.setColouringMode(ColouringMode.Fill);
        
        this.controlPointDrawer.setRadius(3);
        this.controlPointDrawer.setColour(ColourIndex.Stroke, new Colour({h:32, s:1, v:1})); //orange
        this.controlPointDrawer.setColouringMode(ColouringMode.Fill);

        this.controlPointArmDrawer.setColour(new Colour({h:199, s:0.3, v:0.5})); //grey
    }
}